/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:prefer-method-signature

import { ValidationErrors } from '@angular/forms';
import { EntityState, getEntityFieldState, WithEntityState } from '@gh/common-shared-data';
import { createGridModel, EditableCellState, GridModel, GridState } from '@gh/core-ui-grid';
import { coalesce, isObservable } from '@gh/core-util';
import { switchMap$ } from '@gh/rx/operators';
import { constant, noop } from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface GridCellDef {
  required: boolean;
  editable: boolean;
  errors?: ValidationErrors;
}

export interface DefaultGridStateOptions<T> {
  editable?: boolean | Observable<boolean>;
  getId: (entity: T) => any;
  getValue?: (name: string, state: EditableCellState) => any;
  getFieldValue?: (entity: T, field: string) => any;
  validate?: (entity: T, name: string, value: any) => Maybe<ValidationErrors>;
  command?: (entity: T, name: string, value: any) => void;
  getCellDef?: (entity: T, name: string) => GridCellDef;
  valueEquals?: (name: string, oldValue: any, newValue: any) => boolean;
}

const FIELD_GETTER = (entity: any, name: string) => entity[name];
const VALUE_GETTER = (name: string, state: EditableCellState) => state.value;

// tslint:disable-next-line:max-line-length
export function createDefaultGridModel<T extends WithEntityState<any>>(options: DefaultGridStateOptions<any>): GridModel {
  return createGridModel(new DefaultGridState(options));
}

export class DefaultGridState<T extends WithEntityState<E>, E> implements GridState<T> {
  readonly valueEquals?: (name: string, oldValue: any, newValue: any) => boolean;
  readonly getValue: (name: string, state: EditableCellState) => any;

  private editable$$ = new BehaviorSubject<boolean>(false);
  private getId: (entity: T) => any;
  private _validate: (entity: T, name: string, value: any) => Maybe<ValidationErrors>;
  private command: (entity: T, name: string, value: any) => void;
  private getCellDef: (entity: T, name: string) => GridCellDef;
  private getFieldValue: (entity: T, name: string) => any;
  private data: any;
  private rowData$$ = new Subject<any[]>();

  constructor(options: DefaultGridStateOptions<T>) {
    const {
      editable, getId, validate, command, getCellDef, getFieldValue, getValue, valueEquals,
    } = options;

    if (isObservable(editable)) {
      editable.subscribe((value) => this.editable$$.next(value));
    } else {
      this.editable$$.next(coalesce(editable, true));
    }
    this.getId = getId;
    this._validate = validate || constant(void 0);
    this.getValue = getValue || VALUE_GETTER;
    this.getFieldValue = getFieldValue || FIELD_GETTER;
    this.command = command || noop;
    this.getCellDef = getCellDef || constant({ required: false, editable: true });
    this.valueEquals = valueEquals;
  }

  get rowData(): Observable<any[]> {
    return this.rowData$$.pipe(
      switchMap$((data) => isObservable(data) ? data : [data]));
  }

  get editable(): Observable<boolean> {
    return this.editable$$.asObservable();
  }

  setData(data: any): void {
    this.data = data;
    this.rowData$$.next(data);
  }

  getState(entity: T, name: string): EditableCellState {
    const fieldState = getEntityFieldState(entity, name);

    return {
      value: this.getFieldValue(entity, name),
      errors: fieldState && fieldState.errors,
    };
  }

  setValue(entity: T, name: string, value: any): void {
    return this.command(entity, name, value);
  }

  getEntityId(entity: T): any {
    return this.getId(entity);
  }

  isRequired(entity: T, name: string): boolean {
    return this.getCellDef(entity, name).required;
  }

  isEditable(entity: T, name: string): boolean {
    return this.editable$$.value && this.getCellDef(entity, name).editable;
  }

  validate(entity: T, name: string, value: any): Maybe<ValidationErrors> {
    return this._validate(entity, name, value);
  }
}
